/**
 * Lane Operating Hours Service
 * Manages operating schedules for lane types with multiple time slots per day
 */

import { authenticatedFetchJson } from './authenticatedApi';
import { getApiScope } from '../config';
import { msalInstance } from '../contexts/AuthContext';

// Types
export interface LaneOperatingHour {
  lane_operating_hours_id: number;
  day_of_week: number;
  weekday: string;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_date?: string;
  lanetype: {
    lanetype_id: number;
    type_name: string;
    description?: string;
    purpose?: string;
    is_default: boolean;
    is_active: boolean;
  };
  checkpoint: {
    checkpoint_id: number;
    checkpoint_name: string;
    description?: string;
    is_active: boolean;
  };
  terminal: {
    terminal_id: number;
    terminal_name: string;
    is_active: boolean;
  };
}

export interface BulkCreateRequest {
  lanetype_ids: number[];
  day_of_week: number;
  start_time: string;
  end_time: string;
  weekday: string;
}

export interface CreateRequest {
  lanetype_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  weekday: string;
}

export interface UpdateRequest {
  start_time: string;
  end_time: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Helper to get access token
const getAccessToken = async (): Promise<string | null> => {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const response = await msalInstance.acquireTokenSilent({
        scopes: [getApiScope()], // Use scope from .env
        account: accounts[0],
      });
      return response.accessToken;
    }
  } catch (error) {
    console.error('Failed to get access token:', error);
  }
  return null;
};

/**
 * Get all lane operating hours with optional filters
 */
export const getLaneOperatingHours = async (params?: {
  terminal_id?: number;
  checkpoint_id?: number;
  lanetype_id?: number;
  day_of_week?: number;
  include_inactive?: boolean;
}): Promise<ApiResponse<{ operating_hours: LaneOperatingHour[]; total: number }>> => {
  const queryParams = new URLSearchParams();
  
  if (params?.terminal_id) queryParams.append('terminal_id', params.terminal_id.toString());
  if (params?.checkpoint_id) queryParams.append('checkpoint_id', params.checkpoint_id.toString());
  if (params?.lanetype_id) queryParams.append('lanetype_id', params.lanetype_id.toString());
  if (params?.day_of_week) queryParams.append('day_of_week', params.day_of_week.toString());
  if (params?.include_inactive !== undefined) queryParams.append('include_inactive', params.include_inactive.toString());
  
  const endpoint = `/lane-operating-hours/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  return authenticatedFetchJson<ApiResponse<{ operating_hours: LaneOperatingHour[]; total: number }>>(
    endpoint,
    { method: 'GET' },
    getAccessToken
  );
};

/**
 * Get lane operating hours by checkpoint ID
 */
export const getLaneOperatingHoursByCheckpoint = async (
  checkpointId: number,
  params?: { day_of_week?: number; include_inactive?: boolean }
): Promise<ApiResponse<{ checkpoint: any; operating_hours: LaneOperatingHour[]; total: number }>> => {
  const queryParams = new URLSearchParams();
  
  if (params?.day_of_week) queryParams.append('day_of_week', params.day_of_week.toString());
  if (params?.include_inactive !== undefined) queryParams.append('include_inactive', params.include_inactive.toString());
  
  const endpoint = `/lane-operating-hours/checkpoint/${checkpointId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  return authenticatedFetchJson<ApiResponse<{ checkpoint: any; operating_hours: LaneOperatingHour[]; total: number }>>(
    endpoint,
    { method: 'GET' },
    getAccessToken
  );
};

/**
 * Get lane operating hours by terminal ID
 */
export const getLaneOperatingHoursByTerminal = async (
  terminalId: number,
  params?: { day_of_week?: number; include_inactive?: boolean }
): Promise<ApiResponse<{ terminal: any; operating_hours: LaneOperatingHour[]; total: number }>> => {
  const queryParams = new URLSearchParams();
  
  if (params?.day_of_week) queryParams.append('day_of_week', params.day_of_week.toString());
  if (params?.include_inactive !== undefined) queryParams.append('include_inactive', params.include_inactive.toString());
  
  const endpoint = `/lane-operating-hours/terminal/${terminalId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  return authenticatedFetchJson<ApiResponse<{ terminal: any; operating_hours: LaneOperatingHour[]; total: number }>>(
    endpoint,
    { method: 'GET' },
    getAccessToken
  );
};

/**
 * Bulk create operating hours for multiple lane types (ATOMIC TRANSACTION)
 */
export const bulkCreateLaneOperatingHours = async (
  data: BulkCreateRequest
): Promise<ApiResponse<{ created_count: number; lane_operating_hours: any[] }>> => {
  return authenticatedFetchJson<ApiResponse<{ created_count: number; lane_operating_hours: any[] }>>(
    '/lane-operating-hours/bulk',
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    getAccessToken
  );
};

/**
 * Create single lane operating hour
 */
export const createLaneOperatingHour = async (
  data: CreateRequest
): Promise<ApiResponse<any>> => {
  return authenticatedFetchJson<ApiResponse<any>>(
    '/lane-operating-hours/',
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    getAccessToken
  );
};

/**
 * Update lane operating hour (times only)
 */
export const updateLaneOperatingHour = async (
  id: number,
  data: UpdateRequest
): Promise<ApiResponse<any>> => {
  return authenticatedFetchJson<ApiResponse<any>>(
    `/lane-operating-hours/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
    getAccessToken
  );
};

/**
 * Delete lane operating hour (soft delete)
 */
export const deleteLaneOperatingHour = async (
  id: number
): Promise<ApiResponse<any>> => {
  return authenticatedFetchJson<ApiResponse<any>>(
    `/lane-operating-hours/${id}`,
    {
      method: 'DELETE',
    },
    getAccessToken
  );
};
